import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  CheckCircle,
  Activity,
  Clock,
  AlertTriangle,
  Package,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  MapPin,
  X,
  Truck,
  User,
  Bell,
  Settings,
  Search,
  SlidersHorizontal,
  Plus,
  Home,
  ClipboardCheck,
  Send,
  Wallet,
  FileText,
  ArrowLeft,
} from "lucide-react";
import concreteMixerNew from "figma:asset/6c23885f05aee101962aa2c60b22f5549e138b77.png";

interface DriverAppNewProps {
  onQuickAction?: (action: string) => void;
  onClose?: () => void;
}

export function DriverAppNew({ onQuickAction, onClose }: DriverAppNewProps) {
  const [activeTab, setActiveTab] = useState<"home" | "delivery" | "dispatch" | "reports">("home");
  const [selectedVehicle, setSelectedVehicle] = useState<(typeof activeVehicles)[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [isPlusActive, setIsPlusActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: "warning",
      message: "Vehicle TM-223 delayed by 25 mins",
      time: "5 mins ago",
      isRead: false,
    },
    {
      id: 2,
      type: "alert",
      message: "Low cement stock at Plant A",
      time: "12 mins ago",
      isRead: false,
    },
    {
      id: 3,
      type: "info",
      message: "Overdue: Metro Infrastructure ₹2.5L",
      time: "1 hour ago",
      isRead: true,
    },
  ]);

  const activeVehicles = [
    {
      id: "TM-204",
      status: "loading",
      customer: "ABC Constructions",
      eta: "09:30 AM",
      location: "Plant A",
    },
    {
      id: "TM-187",
      status: "on-road",
      customer: "XYZ Developers",
      eta: "10:15 AM",
      location: "Sector 62",
    },
    {
      id: "TM-156",
      status: "at-site",
      customer: "Prime Builders",
      eta: "11:00 AM",
      location: "Site A",
    },
    {
      id: "TM-223",
      status: "delayed",
      customer: "Metro Infrastructure",
      eta: "11:45 AM",
      location: "Highway",
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "loading":
        return "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]";
      case "on-road":
        return "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]";
      case "at-site":
        return "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]";
      case "delayed":
        return "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]";
      default:
        return "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
    }
  };

  const stats = {
    activeVehicles: 5,
    dispatched: 80,
    pendingOrders: 28,
    delayedDeliveries: 5,
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleMarkAsRead = (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
  };

  const handleDeleteNotification = (id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Home/Overview Content
  const HomeContent = () => (
    <div className="pb-24 pt-0">
      {/* KPI Grid - 2x2 Layout */}
      <div className="px-4 mb-6 mt-4">
        <div className="grid grid-cols-2 gap-3">
          {/* Active Vehicles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-4 bg-white"
            style={{
              boxShadow: "0 2px 4px rgba(10, 15, 28, 0.04), 0 1px 2px rgba(10, 15, 28, 0.02)",
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "#F0FDF4" }}
              >
                <img
                  src={concreteMixerNew}
                  alt="Transit Mixer"
                  className="w-5 h-5 object-contain"
                  style={{
                    filter:
                      "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(101%) contrast(97%)",
                  }}
                />
              </div>
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
              >
                <TrendingUp size={12} style={{ color: "#10B981" }} />
                <span style={{ fontSize: "11px", fontWeight: "700", color: "#10B981" }}>12%</span>
              </div>
            </div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                letterSpacing: "-0.03em",
                color: "#0A0F1C",
                marginBottom: "4px",
              }}
            >
              {stats.activeVehicles}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#717182",
                letterSpacing: "-0.01em",
              }}
            >
              Active Vehicles
            </div>
          </motion.div>

          {/* Dispatched Today */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="rounded-2xl p-4 bg-white"
            style={{
              boxShadow: "0 2px 4px rgba(10, 15, 28, 0.04), 0 1px 2px rgba(10, 15, 28, 0.02)",
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "#F0FDF4" }}
              >
                <CheckCircle size={20} style={{ color: "#10B981" }} />
              </div>
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
              >
                <TrendingUp size={12} style={{ color: "#10B981" }} />
                <span style={{ fontSize: "11px", fontWeight: "700", color: "#10B981" }}>8%</span>
              </div>
            </div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                letterSpacing: "-0.03em",
                color: "#0A0F1C",
                marginBottom: "4px",
              }}
            >
              {stats.dispatched}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#717182",
                letterSpacing: "-0.01em",
              }}
            >
              Dispatched Today
            </div>
          </motion.div>

          {/* Pending Orders */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-4 bg-white"
            style={{
              boxShadow: "0 2px 4px rgba(10, 15, 28, 0.04), 0 1px 2px rgba(10, 15, 28, 0.02)",
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "#FEF3C7" }}
              >
                <Clock size={20} style={{ color: "#D97706" }} />
              </div>
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ backgroundColor: "rgba(217, 119, 6, 0.08)" }}
              >
                <TrendingDown size={12} style={{ color: "#D97706" }} />
                <span style={{ fontSize: "11px", fontWeight: "700", color: "#D97706" }}>3%</span>
              </div>
            </div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                letterSpacing: "-0.03em",
                color: "#0A0F1C",
                marginBottom: "4px",
              }}
            >
              {stats.pendingOrders}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#717182",
                letterSpacing: "-0.01em",
              }}
            >
              Pending Orders
            </div>
          </motion.div>

          {/* Delayed Deliveries */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="rounded-2xl p-4 bg-white"
            style={{
              boxShadow: "0 2px 4px rgba(10, 15, 28, 0.04), 0 1px 2px rgba(10, 15, 28, 0.02)",
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "#FEE2E2" }}
              >
                <AlertCircle size={20} style={{ color: "#DC2626" }} />
              </div>
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ backgroundColor: "rgba(220, 38, 38, 0.08)" }}
              >
                <TrendingUp size={12} style={{ color: "#DC2626" }} />
                <span style={{ fontSize: "11px", fontWeight: "700", color: "#DC2626" }}>+2</span>
              </div>
            </div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                letterSpacing: "-0.03em",
                color: "#0A0F1C",
                marginBottom: "4px",
              }}
            >
              {stats.delayedDeliveries}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#717182",
                letterSpacing: "-0.01em",
              }}
            >
              Delayed Deliveries
            </div>
          </motion.div>
        </div>
      </div>

      {/* Active Deliveries */}
      <div className="px-4 py-6 bg-[#F6F7F9]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-5"
          style={{
            boxShadow: "0 2px 4px rgba(10, 15, 28, 0.04), 0 1px 2px rgba(10, 15, 28, 0.02)",
            borderTop: "3px solid #10B981",
          }}
        >
          <div className="flex items-center justify-between mb-5">
            <h2
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#00315c",
                letterSpacing: "-0.01em",
              }}
            >
              Active Deliveries
            </h2>
            <button
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#10B981",
              }}
            >
              View All
            </button>
          </div>

          <div className="space-y-3">
            {activeVehicles.slice(0, 3).map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.05 }}
                className="rounded-xl p-4 cursor-pointer transition-all active:scale-[0.98]"
                style={{
                  backgroundColor: "#F0FDF4",
                }}
                onClick={() => {
                  setSelectedVehicle(vehicle);
                  setIsModalOpen(true);
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#00315c",
                        marginBottom: "4px",
                      }}
                    >
                      {vehicle.id}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#00315c",
                        opacity: 0.7,
                        marginBottom: "2px",
                      }}
                    >
                      {vehicle.customer}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#00315c",
                        opacity: 0.6,
                      }}
                    >
                      {vehicle.location}
                    </div>
                  </div>
                  <span
                    className={`text-xs px-3 py-1.5 rounded-xl inline-flex items-center gap-1.5 ${getStatusColor(
                      vehicle.status
                    )}`}
                    style={{
                      fontSize: "10px",
                      fontWeight: "600",
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor:
                          vehicle.status === "at-site"
                            ? "#10B981"
                            : vehicle.status === "on-road"
                            ? "#3B82F6"
                            : vehicle.status === "loading"
                            ? "#F59E0B"
                            : "#DC2626",
                      }}
                    />
                    {vehicle.status}
                  </span>
                </div>

                <div
                  className="flex items-center gap-1 pt-2"
                  style={{
                    borderTop: "1px solid rgba(16, 185, 129, 0.15)",
                  }}
                >
                  <Clock size={12} style={{ color: "#10B981" }} />
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                      color: "#00315c",
                      opacity: 0.7,
                    }}
                  >
                    ETA: {vehicle.eta}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-[#F6F7F9] w-full overflow-x-hidden">
        <div className="w-full max-w-md mx-auto">
          {/* Floating Back Button */}
          {onClose && (
            <motion.button
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              onClick={onClose}
              className="fixed top-4 right-4 z-[90] w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-xl"
              style={{
                backgroundColor: "rgba(0, 49, 92, 0.95)",
                boxShadow: "0 4px 12px rgba(0, 49, 92, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <ArrowLeft size={20} color="#ffffff" strokeWidth={2.5} />
            </motion.button>
          )}
          {/* ===== HEADER (Exact from specification) ===== */}
          <div className="bg-white">
            {/* Top Header Section */}
            <div className="px-4 pt-3 pb-3 flex items-center justify-between">
              {/* Left Section - Logo & Title */}
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#00315c" }}
                >
                  <img
                    src={concreteMixerNew}
                    alt="Logo"
                    className="w-6 h-6 object-contain"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#00315c",
                    }}
                  >
                    Track Concrete
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#00315c",
                      opacity: 0.6,
                    }}
                  >
                    {activeTab === "home"
                      ? "Driver Dashboard"
                      : activeTab === "delivery"
                      ? "Delivery Management"
                      : activeTab === "dispatch"
                      ? "Dispatch Center"
                      : "Reports & Analytics"}
                  </div>
                </div>
              </div>

              {/* Right Section - Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Notification Button */}
                <button
                  className="relative p-2 rounded-full hover:bg-gray-100 transition-all"
                  onClick={() => setShowNotificationPanel(true)}
                >
                  <Bell size={22} style={{ color: "#00315c" }} />
                  {unreadCount > 0 && (
                    <div
                      className="absolute w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: "#FF6B6B",
                        top: "6px",
                        right: "6px",
                      }}
                    />
                  )}
                </button>

                {/* Profile/Settings Button */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: "#ffffff",
                    border: "2px solid #e5e7eb",
                  }}
                >
                  <Settings size={18} style={{ color: "#00315c" }} />
                </div>
              </div>
            </div>

            {/* Search Bar Section */}
            <div className="px-4 pb-3">
              <div
                className="rounded-3xl px-4 py-3 flex items-center gap-2 focus-within:ring-2 focus-within:ring-[#00315c] transition-all"
                style={{ backgroundColor: "#F6F7F9" }}
              >
                <Search size={20} className="text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vehicles, orders, drivers..."
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "#00315c" }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="p-1.5 rounded-lg hover:bg-white transition-all">
                  <SlidersHorizontal size={18} style={{ color: "#00315c" }} />
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <AnimatePresence mode="wait">
            {activeTab === "home" && <HomeContent key="home" />}
            {activeTab === "delivery" && (
              <div key="delivery" className="px-4 pt-6 pb-24">
                <div className="bg-white rounded-2xl p-6 text-center">
                  <div
                    className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: "#F0FDF4" }}
                  >
                    <Truck size={32} style={{ color: "#10B981" }} />
                  </div>
                  <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#0A0F1C" }}>
                    Delivery Tracking
                  </h3>
                  <p style={{ fontSize: "14px", color: "#717182", marginTop: "8px" }}>
                    Monitor all delivery vehicles in real-time
                  </p>
                </div>
              </div>
            )}
            {activeTab === "dispatch" && (
              <div key="dispatch" className="px-4 pt-6 pb-24">
                <div className="bg-white rounded-2xl p-6 text-center">
                  <div
                    className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: "#F0FDF4" }}
                  >
                    <Send size={32} style={{ color: "#10B981" }} />
                  </div>
                  <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#0A0F1C" }}>
                    Dispatch Management
                  </h3>
                  <p style={{ fontSize: "14px", color: "#717182", marginTop: "8px" }}>
                    Manage vehicle dispatches and routing
                  </p>
                </div>
              </div>
            )}
            {activeTab === "reports" && (
              <div key="reports" className="px-4 pt-6 pb-24">
                <div className="bg-white rounded-2xl p-6 text-center">
                  <div
                    className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: "#F0FDF4" }}
                  >
                    <FileText size={32} style={{ color: "#10B981" }} />
                  </div>
                  <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#0A0F1C" }}>
                    Reports & Analytics
                  </h3>
                  <p style={{ fontSize: "14px", color: "#717182", marginTop: "8px" }}>
                    Access comprehensive delivery reports
                  </p>
                </div>
              </div>
            )}
          </AnimatePresence>

          {/* ===== FOOTER NAVIGATION (Exact from specification) ===== */}
          <div
            className="fixed bottom-0 left-0 right-0 transition-colors"
            style={{ zIndex: 70, transitionDuration: "300ms" }}
          >
            <div className="w-full max-w-md mx-auto">
              {/* Floating + Button */}
              <div
                className="absolute left-1/2 -translate-x-1/2 -top-5"
                style={{ zIndex: 80 }}
              >
                {/* Glow Effect */}
                <div
                  className="absolute w-20 h-20 rounded-full blur-lg opacity-40"
                  style={{
                    backgroundColor: "#ffffff",
                    transform: "translateX(-16px) translateY(-16px)",
                  }}
                />
                {/* Plus Button */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsPlusActive(!isPlusActive)}
                  className="relative w-16 h-16 rounded-full flex items-center justify-center transition-all shadow-lg"
                  style={{
                    backgroundColor: isPlusActive ? "#9CA3AF" : "#00315c",
                    boxShadow:
                      "0 8px 20px rgba(0, 49, 92, 0.25), 0 4px 8px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <motion.div
                    animate={{ rotate: isPlusActive ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Plus size={32} color="#ffffff" strokeWidth={2.5} />
                  </motion.div>
                </motion.button>
              </div>

              {/* Navigation Bar with SVG Curved Notch */}
              <div className="relative h-16">
                {/* SVG Background with curved notch */}
                <svg
                  viewBox="0 0 390 64"
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ zIndex: -1 }}
                >
                  <defs>
                    <filter id="shadow">
                      <feDropShadow dx="0" dy="-2" stdDeviation="4" floodOpacity="0.08" />
                    </filter>
                  </defs>
                  {/* Main background with notch */}
                  <path
                    d="M 0,16 L 0,64 L 390,64 L 390,16 L 390,0 L 225,0 Q 218,0 215,3 Q 205,12 195,16 Q 185,12 175,3 Q 172,0 165,0 L 0,0 L 0,16 Z"
                    fill="#ffffff"
                    filter="url(#shadow)"
                  />
                  {/* Top border */}
                  <path
                    d="M 0,0 L 165,0 Q 172,0 175,3 Q 185,12 195,16 Q 205,12 215,3 Q 218,0 225,0 L 390,0"
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    fill="none"
                  />
                </svg>

                {/* Navigation Items */}
                <div className="h-16 max-w-screen-2xl mx-auto px-4 pt-4 flex items-end justify-between">
                  {/* Left Section */}
                  <div className="flex gap-2 flex-1">
                    <button
                      onClick={() => setActiveTab("home")}
                      className="flex flex-col items-center gap-1 px-4 py-2 min-w-0 transition-all active:scale-95"
                    >
                      <Home
                        size={22}
                        style={{
                          color: activeTab === "home" ? "#00315c" : "#9CA3AF",
                        }}
                      />
                      <span
                        className="text-[10px] sm:text-xs text-center leading-tight w-full truncate"
                        style={{
                          color: activeTab === "home" ? "#00315c" : "#9CA3AF",
                        }}
                      >
                        Home
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab("delivery")}
                      className="flex flex-col items-center gap-1 px-4 py-2 min-w-0 transition-all active:scale-95"
                    >
                      <ClipboardCheck
                        size={22}
                        style={{
                          color: activeTab === "delivery" ? "#00315c" : "#9CA3AF",
                        }}
                      />
                      <span
                        className="text-[10px] sm:text-xs text-center leading-tight w-full truncate"
                        style={{
                          color: activeTab === "delivery" ? "#00315c" : "#9CA3AF",
                        }}
                      >
                        Delivery
                      </span>
                    </button>
                  </div>

                  {/* Center Spacer */}
                  <div className="w-16" />

                  {/* Right Section */}
                  <div className="flex gap-2 flex-1 justify-end">
                    <button
                      onClick={() => setActiveTab("dispatch")}
                      className="flex flex-col items-center gap-1 px-4 py-2 min-w-0 transition-all active:scale-95"
                    >
                      <Send
                        size={22}
                        style={{
                          color: activeTab === "dispatch" ? "#00315c" : "#9CA3AF",
                        }}
                      />
                      <span
                        className="text-[10px] sm:text-xs text-center leading-tight w-full truncate"
                        style={{
                          color: activeTab === "dispatch" ? "#00315c" : "#9CA3AF",
                        }}
                      >
                        Dispatch
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab("reports")}
                      className="flex flex-col items-center gap-1 px-4 py-2 min-w-0 transition-all active:scale-95"
                    >
                      <Wallet
                        size={22}
                        style={{
                          color: activeTab === "reports" ? "#00315c" : "#9CA3AF",
                        }}
                      />
                      <span
                        className="text-[10px] sm:text-xs text-center leading-tight w-full truncate"
                        style={{
                          color: activeTab === "reports" ? "#00315c" : "#9CA3AF",
                        }}
                      >
                        Reports
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Details Modal (same as before) */}
      <AnimatePresence>
        {isModalOpen && selectedVehicle && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-xl">
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl" style={{ backgroundColor: "#F0FDF4" }}>
                      <Truck size={20} style={{ color: "#10B981" }} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#00315c" }}>
                        Vehicle Details
                      </h3>
                      <p style={{ fontSize: "12px", color: "#00315c", opacity: 0.6 }}>
                        Delivery Information
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 rounded-xl hover:bg-gray-100"
                  >
                    <X size={18} style={{ color: "#717182" }} />
                  </button>
                </div>

                <div className="p-5 space-y-3">
                  <div className="rounded-xl p-3.5" style={{ backgroundColor: "#F0FDF4" }}>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <Truck size={16} style={{ color: "#10B981" }} />
                        <span style={{ fontSize: "13px", color: "#717182" }}>Vehicle ID</span>
                      </div>
                      <span style={{ fontSize: "14px", fontWeight: "700", color: "#00315c" }}>
                        {selectedVehicle.id}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontSize: "13px", color: "#717182" }}>Status</span>
                      <span
                        className={`text-xs px-2.5 py-1 rounded-xl ${getStatusColor(
                          selectedVehicle.status
                        )}`}
                      >
                        {selectedVehicle.status}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-xl p-3.5" style={{ backgroundColor: "#F0FDF4" }}>
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} style={{ color: "#10B981" }} />
                      <span style={{ fontSize: "13px", color: "#717182" }}>Customer</span>
                    </div>
                    <p className="ml-6" style={{ fontSize: "14px", fontWeight: "700", color: "#00315c" }}>
                      {selectedVehicle.customer}
                    </p>
                  </div>

                  <div className="rounded-xl p-3.5" style={{ backgroundColor: "#F0FDF4" }}>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={16} style={{ color: "#10B981" }} />
                      <span style={{ fontSize: "13px", color: "#717182" }}>Location</span>
                    </div>
                    <p className="ml-6" style={{ fontSize: "14px", fontWeight: "700", color: "#00315c" }}>
                      {selectedVehicle.location}
                    </p>
                  </div>

                  <div className="rounded-xl p-3.5" style={{ backgroundColor: "#F0FDF4" }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={16} style={{ color: "#10B981" }} />
                      <span style={{ fontSize: "13px", color: "#717182" }}>ETA</span>
                    </div>
                    <p className="ml-6" style={{ fontSize: "14px", fontWeight: "700", color: "#00315c" }}>
                      {selectedVehicle.eta}
                    </p>
                  </div>
                </div>

                <div className="p-5 border-t border-gray-200 flex gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 py-3 rounded-xl"
                    style={{ backgroundColor: "#F9FAFB", color: "#717182", fontWeight: "700" }}
                  >
                    Close
                  </button>
                  <button
                    className="flex-1 py-3 rounded-xl"
                    style={{
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      color: "white",
                      fontWeight: "700",
                      boxShadow: "0 4px 8px rgba(16, 185, 129, 0.25)",
                    }}
                    onClick={() => setIsModalOpen(false)}
                  >
                    Track Vehicle
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Notification Panel (simplified) */}
      <AnimatePresence>
        {showNotificationPanel && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNotificationPanel(false)}
              className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl max-w-md mx-auto"
              style={{ maxHeight: "80vh" }}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#00315c" }}>
                    Notifications
                  </h2>
                  {unreadCount > 0 && (
                    <span
                      className="px-3 py-1 rounded-full"
                      style={{ backgroundColor: "#FEE2E2", color: "#DC2626", fontSize: "12px", fontWeight: "700" }}
                    >
                      {unreadCount} NEW
                    </span>
                  )}
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className="p-4 rounded-xl"
                      style={{
                        backgroundColor: notif.isRead ? "#F9FAFB" : "#F0FDF4",
                        borderLeft: notif.isRead ? "none" : "3px solid #10B981",
                      }}
                    >
                      <p style={{ fontSize: "14px", fontWeight: "600", color: "#00315c" }}>
                        {notif.message}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <span style={{ fontSize: "11px", color: "#717182" }}>{notif.time}</span>
                        <div className="flex gap-2">
                          {!notif.isRead && (
                            <button
                              onClick={() => handleMarkAsRead(notif.id)}
                              style={{ fontSize: "11px", color: "#10B981", fontWeight: "600" }}
                            >
                              Mark read
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteNotification(notif.id)}
                            className="p-1 rounded"
                            style={{ backgroundColor: "#FEE2E2" }}
                          >
                            <X size={12} style={{ color: "#DC2626" }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowNotificationPanel(false)}
                  className="w-full mt-4 py-3 rounded-xl"
                  style={{
                    backgroundColor: "#00315c",
                    color: "white",
                    fontWeight: "700",
                  }}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}